import {
  Box,
  Button,
  Flex,
  Typography,
  MediaBox,
  Picture,
} from "@jewlr/storybook/core"
import { FloatInput } from "@jewlr/storybook/form"
import React, { useState } from "react"
import { useForm, Controller } from "react-hook-form"
import styled from "styled-components"

import { SectionList } from "@jewlr/storybook/info"
import { jewlr_t } from "helpers/application"
import { to_currency } from "helpers/number"

import GiftCardModal from "./gift-card-modal"

const Title = styled(Typography).attrs({
  as: "h1",
  fontSize: 26,
  fontWeight: "500",
  mb: 2,
})``
const GiftCardLink = styled(Typography).attrs((props) => ({
  as: "a",
  fontWeight: props.theme.infoGiftCards.giftCardLink.fontWeight,
  href: props.to,
}))`
  text-decoration: underline;
  ${({ theme }) => `
    display: block;
    color: ${theme.colors.primary};
    font-size: ${theme.fontSizes[14]};
    ${theme.mediaQueries.tablet`
      font-size: ${theme.fontSizes[14]};
  `};

  `}
`

const ContinueButton = styled(Button).attrs((props) => ({
  fontSize: props.theme.infoGiftCards.continueButton.fontSize,
  fontWeight: props.theme.infoGiftCards.continueButton.fontWeight,
  ...props,
}))`
  text-transform: ${(props) => props.theme.infoGiftCards.textTransform};
`
const FlexBlock = styled(Flex).attrs((props) => ({
  justifyContent: {
    _: "center",
    desktop: props.theme.infoGiftCards.flexBlock.justifyContent,
  },
  ...props,
}))``

const FlexImageBox = styled(Box).attrs((props) => ({
  flexBasis: {
    _: "90%",
    desktop: props.theme.infoGiftCards.flexImageBox.flexBasis,
  },
}))`
  text-align: center;
`
const FlexTextBox = styled(Box).attrs((props) => ({
  flexBasis: {
    _: "90%",
    desktop: props.theme.infoGiftCards.flexTextBox.flexBasis,
  },
}))`
  text-align: center;
`

const InputWrapper = styled(FloatInput).attrs((props) => ({
  ...props,
}))`
  text-align: left;
`
const FieldWrapper = styled(Box).attrs((props) => ({
  mb: "16px",
  ...props,
}))`
  text-align: left;
`
const BulletList = styled.li`
  padding-left: 8px;
`
const MainContainer = styled(Box).attrs((props) => ({
  bg: props.theme.infoGiftCards.mainContainer.bg,
  ...props,
}))``

const MobileTitleDescription = styled(MediaBox).attrs((props) => ({
  lessThan: "tablet",
  mt: props.theme.infoGiftCards.mobileTitleDescription.mt,
  textAlign: "center",
  ...props,
}))``

const GiftCard = () => {
  const [amount, setAmount] = useState("")
  const [toggleHalfModal, setToggleHalfModal] = useState(false)

  const {
    control,
    formState: { isSubmitting },
    handleSubmit,
  } = useForm({
    defaultValues: {
      amount: "",
    },
    mode: "onBlur",
  })

  const onSubmit = (values) => {
    let amount = values.amount
    if (amount.includes(sl.config.default_currency)) {
      setAmount(amount)
    } else {
      setAmount(`${sl.config.default_currency}${amount}`)
    }

    setToggleHalfModal(true)
  }

  return (
    <MainContainer mb={2} pb={7} pt={{ tablet: 7 }}>
      <Box contain>
        <FlexBlock alignItems="center" flexWrap="wrap" gutter={{ tablet: 3 }}>
          <FlexImageBox>
            <MobileTitleDescription>
              <Title>Give the Gift of Choice</Title>
              <Typography lineHeight="22px" mb={4} px="7px">
                Shopping for someone special but not sure what to buy? Let them{" "}
                {jewlr_t("terms.personalize")} just the right gift with a{" "}
                {sl.config.site_title} E-Gift Card – perfect for any occasion,
                including last-minute gifting!{" "}
              </Typography>
            </MobileTitleDescription>
            <Picture
              alt="Jewlr Gift Cards"
              fallback={require("images/info/gift_cards/jewlr-giftcard.jpg")}
              srcSet={[
                {
                  height: 358,
                  media: "(max-width: 767px)",
                  sizes: "100vw",
                  srcSet: `
                    ${require("images/info/gift_cards/m-jewlr-giftcard.jpg")} 375w,
                    ${require("images/info/gift_cards/m-jewlr-giftcard-2x.jpg")} 750w
                  `,
                  width: 375,
                },
                {
                  height: 454,
                  media: "(min-width: 768px)",
                  sizes: "(max-width: 1023px) 630px, 472px",
                  srcSet: `
                    ${require("images/info/gift_cards/jewlr-giftcard.jpg")} 472w,
                    ${require("images/info/gift_cards/jewlr-giftcard-2x")} 944w
                  `,
                  width: 472,
                },
              ]}
            />
          </FlexImageBox>
          <FlexTextBox>
            <MediaBox greaterThanOrEqual="tablet">
              <Title mt={{ _: 5, desktop: 0 }}>Give the Gift of Choice</Title>
              <Typography lineHeight="22px" mb={4} px="7px">
                Shopping for someone special but not sure what to buy? Let them{" "}
                {jewlr_t("terms.personalize")} just the right gift with a{" "}
                {sl.config.site_title} E-Gift Card – perfect for any occasion,
                including last-minute gifting!{" "}
              </Typography>
            </MediaBox>
            <form onSubmit={handleSubmit(onSubmit)}>
              <FieldWrapper
                mt={{ _: 5, tablet: 0 }}
                mx="auto"
                textAlign="center"
                width="90%"
              >
                <Controller
                  control={control}
                  name="amount"
                  render={({ field, fieldState: { error } }) => (
                    <InputWrapper
                      {...field}
                      data-cy="info-giftcard-amount-field"
                      error={error}
                      label="Gift Card Amount"
                      onChange={(e) => {
                        let { value } = e.target
                        let amount = value.replace(
                          sl.config.default_currency,
                          ""
                        )
                        amount = `${sl.config.default_currency}${amount}`
                        field.onChange(amount)
                      }}
                      placeholder={` Enter Gift Card Amount (${sl.config.currency_code})`}
                      textAlign="center"
                    />
                  )}
                  rules={{
                    validate: (amount) => {
                      let _amount = amount.replace(
                        sl.config.default_currency,
                        ""
                      )
                      if (_amount < 50) {
                        return `Please provide an amount of at least ${jewlr_t(
                          "number.currency.format.unit"
                        )}50`
                      } else if (_amount > 1000) {
                        return `Please provide an amount between ${jewlr_t(
                          "number.currency.format.unit"
                        )}50 and ${jewlr_t("number.currency.format.unit")}1000`
                      } else if (!/^\d+$/.test(_amount)) {
                        return "Please specify a valid number"
                      }
                    },
                  }}
                />
              </FieldWrapper>
              <ContinueButton
                data-cy="info-giftcard-continue-btn"
                loading={isSubmitting}
                mb={{ _: "56px", tablet: 4 }}
                p={{ _: "12px 20px", tablet: "12px 32px" }}
                sz="md"
                type="submit"
                variant="primary"
                width="90%"
              >
                continue
              </ContinueButton>
            </form>
            <Box pl="5%" textAlign="left">
              <Typography fontSize="14px">
                WHY CHOOSE {sl.config.site_title.toUpperCase()} E-GIFT CARDS:
              </Typography>
              <SectionList fontSize="14px" lineHeight="22px">
                <BulletList>
                  Available in any amount of your choice (min. {to_currency(50)}
                  )
                </BulletList>
                <BulletList>You choose the delivery date and time</BulletList>
                <BulletList>
                  Delivered via email with complete instructions
                </BulletList>
                <BulletList>Never expire and no service fees</BulletList>
              </SectionList>
              <GiftCardLink
                data-lc="gift-card"
                my={2}
                target="_blank"
                to="/info/terms-conditions#gift_card"
              >
                Gift Cards Terms & Conditions
              </GiftCardLink>
            </Box>
          </FlexTextBox>
        </FlexBlock>
        <GiftCardModal
          amount={amount}
          onClose={() => setToggleHalfModal(false)}
          show={toggleHalfModal}
        />
      </Box>
    </MainContainer>
  )
}

export default GiftCard
